import type { NonEmptyArray } from '@mntn/zod'
import { match } from 'ts-pattern'
import { z } from 'zod'
import type { IsMntn } from './actor.ts'
import type { AuthorizedAdvertiserId, AuthorizedAdvertiserIdBrand } from './authorized-advertiser-id.ts'
import type { AuthorizedAdvertiserUrl, AuthorizedAdvertiserUrlBrand } from './authorized-advertiser-url.ts'
import type { Principal } from './principal.ts'

function canAccessAdvertiserIds({
  principal,
  advertiserIds,
}: {
  principal: Principal
  advertiserIds: ReadonlyArray<number>
}) {
  if (principal.actor.isMntn) return true
  const { tenant } = principal

  return match(tenant)
    .with({ kind: 'advertiser' }, advertiserTenant =>
      advertiserIds.every(
        advertiserId =>
          advertiserId === advertiserTenant.current.advertiserId ||
          advertiserTenant.advertiserIds.includes(advertiserId),
      ),
    )
    .with({ kind: 'none' }, noneTenant =>
      advertiserIds.every(advertiserId => noneTenant.advertiserIds.includes(advertiserId)),
    )
    .with({ kind: 'publisher' }, () => false)
    .exhaustive()
}

export function authorizeAdvertiserId(input: {
  principal: Principal & IsMntn
  advertiserId: number
}): AuthorizedAdvertiserId
export function authorizeAdvertiserId(input: {
  principal: Principal
  advertiserId: number
}): AuthorizedAdvertiserId | undefined
export function authorizeAdvertiserId({ principal, advertiserId }: { principal: Principal; advertiserId: number }) {
  if (!canAccessAdvertiserIds({ principal, advertiserIds: [advertiserId] })) {
    return undefined
  }

  return AuthorizedAdvertiserIdSchema.parse(advertiserId)
}

export function authorizeAdvertiserIdFromAccess(input: {
  access: { advertiserIds: ReadonlyArray<number>; isMntn: true }
  advertiserId: number
}): AuthorizedAdvertiserId
export function authorizeAdvertiserIdFromAccess(input: {
  access: { advertiserIds: ReadonlyArray<number>; isMntn: boolean }
  advertiserId: number
}): AuthorizedAdvertiserId | undefined
export function authorizeAdvertiserIdFromAccess({
  access,
  advertiserId,
}: {
  access: { advertiserIds: ReadonlyArray<number>; isMntn: boolean }
  advertiserId: number
}) {
  if (!access.isMntn && !access.advertiserIds.includes(advertiserId)) {
    return undefined
  }

  return AuthorizedAdvertiserIdSchema.parse(advertiserId)
}

export function createAuthorizeAdvertiserIdTransform<Request extends { principal: Principal }, AuthorizedRequest>(
  advertiserIdFrom: (request: Request) => number,
  withAuthorizedAdvertiserId: (request: Request, advertiserId: AuthorizedAdvertiserId) => AuthorizedRequest,
) {
  return (request: Request, context: z.RefinementCtx) => {
    const advertiserId = authorizeAdvertiserId({
      principal: request.principal,
      advertiserId: advertiserIdFrom(request),
    })
    if (!advertiserId) {
      context.addIssue({
        code: 'custom',
        message: 'Advertiser access required',
      })
      return z.NEVER
    }

    return withAuthorizedAdvertiserId(request, advertiserId)
  }
}

/**
 * Authorizes the advertiser id, then the advertiser URL the request names for it, and hands the mapper both.
 * An advertiser tenant may name only its own URL; an MNTN actor may name any usable URL or bare domain.
 * Anything else rejects the request.
 */
export function createAuthorizeAdvertiserTransform<Request extends { principal: Principal }, AuthorizedRequest>(
  advertiserIdFrom: (request: Request) => number,
  advertiserUrlFrom: (request: Request) => string,
  withAuthorizedAdvertiser: (
    request: Request,
    advertiser: { advertiserId: AuthorizedAdvertiserId; advertiserUrl: AuthorizedAdvertiserUrl },
  ) => AuthorizedRequest,
) {
  return (request: Request, context: z.RefinementCtx) => {
    const advertiserId = authorizeAdvertiserId({
      principal: request.principal,
      advertiserId: advertiserIdFrom(request),
    })
    if (!advertiserId) {
      context.addIssue({ code: 'custom', message: 'Advertiser access required' })
      return z.NEVER
    }

    const advertiserUrl = authorizeRequestAdvertiserUrl({
      principal: request.principal,
      url: advertiserUrlFrom(request),
    })
    if (!advertiserUrl) {
      context.addIssue({ code: 'custom', message: 'Advertiser URL is not usable' })
      return z.NEVER
    }

    return withAuthorizedAdvertiser(request, { advertiserId, advertiserUrl })
  }
}

export function createAuthorizeAdvertiserIdsTransform<Request extends { principal: Principal }, AuthorizedRequest>(
  advertiserIdsFrom: (request: Request) => Readonly<NonEmptyArray<number>>,
  withAuthorizedAdvertiserIds: (
    request: Request,
    advertiserIds: NonEmptyArray<AuthorizedAdvertiserId>,
  ) => AuthorizedRequest,
) {
  return (request: Request, context: z.RefinementCtx) => {
    const advertiserIds = authorizeAdvertiserIds({
      principal: request.principal,
      advertiserIds: advertiserIdsFrom(request),
      context,
    })
    if (!advertiserIds) return z.NEVER

    return withAuthorizedAdvertiserIds(request, advertiserIds)
  }
}

export function authorizeAdvertiserIds(input: {
  principal: Principal
  advertiserIds: Readonly<NonEmptyArray<number>>
  context: z.RefinementCtx
}): NonEmptyArray<AuthorizedAdvertiserId> | undefined
export function authorizeAdvertiserIds(input: {
  principal: Principal
  advertiserIds: ReadonlyArray<number>
  context: z.RefinementCtx
}): AuthorizedAdvertiserId[] | undefined
export function authorizeAdvertiserIds({
  principal,
  advertiserIds,
  context,
}: {
  principal: Principal
  advertiserIds: ReadonlyArray<number>
  context: z.RefinementCtx
}) {
  if (!canAccessAdvertiserIds({ principal, advertiserIds })) {
    context.addIssue({
      code: 'custom',
      message: 'Advertiser access required',
    })
    return undefined
  }

  return advertiserIds.map(advertiserId => AuthorizedAdvertiserIdSchema.parse(advertiserId))
}

export function authorizeAdvertiserIdBodyTransform<
  Request extends {
    body: { advertiserId: number }
    principal: Principal
  },
>(request: Request, context: z.RefinementCtx) {
  return createAuthorizeAdvertiserIdTransform(
    (input: Request) => input.body.advertiserId,
    (input, advertiserId) => ({
      ...input,
      body: { ...input.body, advertiserId },
    }),
  )(request, context)
}

export function authorizeAdvertiserIdsQueryTransform<
  Request extends {
    query: { 'advertiser-ids': NonEmptyArray<number> }
    principal: Principal
  },
>(request: Request, context: z.RefinementCtx) {
  const advertiserIds = authorizeAdvertiserIds({
    principal: request.principal,
    advertiserIds: request.query['advertiser-ids'],
    context,
  })
  if (!advertiserIds) return z.NEVER

  return {
    ...request,
    query: { ...request.query, 'advertiser-ids': advertiserIds },
  }
}

/** Brands the URL gary holds for an already-authorized advertiser. Undefined when gary's record is another advertiser's or its URL is unusable. */
export function authorizeAdvertiserUrl({
  advertiserId,
  garyAdvertiser,
}: {
  advertiserId: AuthorizedAdvertiserId
  garyAdvertiser: { advertiserId: number; url: string | null | undefined }
}): AuthorizedAdvertiserUrl | undefined {
  if (garyAdvertiser.advertiserId !== advertiserId) return undefined

  const result = AuthorizedAdvertiserUrlSchema.safeParse(garyAdvertiser.url)
  return result.success ? result.data : undefined
}

function authorizeRequestAdvertiserUrl({ principal, url }: { principal: Principal; url: string }) {
  if (principal.tenant.kind === 'advertiser' && url === principal.tenant.current.url) {
    return principal.tenant.current.url
  }

  return authorizeAdvertiserUrlFromAccess({ url, access: principal.actor })
}

/** Brands a caller-supplied URL or bare domain (a bare domain gets `https://`). Only MNTN callers may supply one; undefined when it is unusable. */
export function authorizeAdvertiserUrlFromAccess({
  url,
  access,
}: {
  url: string
  access: { isMntn: boolean }
}): AuthorizedAdvertiserUrl | undefined {
  if (!access.isMntn) return undefined

  const result = AuthorizedAdvertiserUrlSchema.safeParse(/^https?:\/\//i.test(url) ? url : `https://${url}`)
  return result.success ? result.data : undefined
}

// NEVER export this schema; advertiser IDs must pass an authorization check.
const AuthorizedAdvertiserIdSchema = z.number().brand<AuthorizedAdvertiserIdBrand>()

// NEVER export this schema; advertiser URLs must pass authorizeAdvertiserUrl.
const AuthorizedAdvertiserUrlSchema = z.url({ protocol: /^https?$/ }).brand<AuthorizedAdvertiserUrlBrand>()

/** Stands in for the advertiser URL on tenants built for service callers, which have no gary record. `.invalid` never resolves. */
export const serviceAdvertiserUrl: AuthorizedAdvertiserUrl = AuthorizedAdvertiserUrlSchema.parse(
  'https://service-client.invalid',
)
